// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { Injectable } from "@angular/core";
import { ACTION_TARGET, Activity, Careplan, ICareplan } from "../models/careplan";
import { ACTIVITY_CATEGORY, ACTIVITY_LOCATION, Reference, SCHEDULE_PERIOD, Timing } from "../models/sharedInterfaces";
import { ArrayHelper } from "./array-helper";
import { FileLogger } from "./fileLogger";

/**
 * Helper class for FHIR format
 */
@Injectable()
// tslint:disable-next-line: class-name
export class FHIR_ActivityHelper {
  /**
   * cast a Timing.PeriodUnits into SCHEDULE_PERIOD (string to enum)
   */
  public static getScheduledPeriod(timing: Timing): SCHEDULE_PERIOD {
    if (!timing || !timing.periodUnits) return SCHEDULE_PERIOD.DAY;

    switch (timing.periodUnits.toLowerCase()) {
      case "min":
        return SCHEDULE_PERIOD.MINUTE;
      case "h":
        return SCHEDULE_PERIOD.HOUR;
      case "d":
        return SCHEDULE_PERIOD.DAY;
      case "w":
      case "wk":
        return SCHEDULE_PERIOD.WEEK;
      case "m":
        return SCHEDULE_PERIOD.MONTH;
      case "y":
        return SCHEDULE_PERIOD.YEAR;
      default:
        return SCHEDULE_PERIOD.DAY;
    }
  }

  /**
   * return code of this activity
   */
  public static getCode(activity: Activity): string {
    try {
      return activity.detail.code.coding[0].code;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET CODE:", err);
      return "";
    }
  }

  /**
   *  return display name of this activity
   */
  public static getName(activity: Activity): string {
    try {
      return activity.detail.code.text ? activity.detail.code.text : activity.detail.code.coding[0].display;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET NAME:", err);
      return "";
    }
  }

  public static getProductDescription(activity: Activity, name: string): string {
    try {
      const description = activity.detail.productReference ? activity.detail.productReference.display : activity.detail.description;
      return !(description === name) ? description : "";
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "ActivityTimeline.getProductDescription:", JSON.stringify(err));
      return "";
    }
  }

  /**
   *  return name of this activity
   */
  public static getDescription(activity: Activity): string {
    try {
      return activity.detail.description;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET DESCRIPTION:", err);
      return "";
    }
  }

  /**
   * return activity start date
   */
  public static getStartDate(activity: Activity): string {
    try {
      if (activity.detail.scheduledPeriod) return activity.detail.scheduledPeriod.start;
      if (activity.detail.scheduledTiming) return activity.detail.scheduledTiming.repeat.boundsPeriod.start;
      if (activity.detail.scheduledString) return activity.detail.scheduledString;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET STARTDATE:", err);
      return null;
    }
  }

  /**
   * return activity end date
   */
  public static getEndDate(activity: Activity): string {
    try {
      if (activity.detail.scheduledPeriod) return activity.detail.scheduledPeriod.end;
      if (activity.detail.scheduledTiming) return activity.detail.scheduledTiming.repeat.boundsPeriod.end;
      if (activity.detail.scheduledString) return activity.detail.scheduledString;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET ENDDATE:", err);
      return null;
    }
  }

  /**
   * return activity location
   */
  public static getLocation(activity: Activity): ACTIVITY_LOCATION {
    try {
      const location = activity.detail.location as Reference;
      if (location) {
        switch (location.reference) {
          case "Location/Home":
            return ACTIVITY_LOCATION.HOME;
          case "Location/Hospital":
            return ACTIVITY_LOCATION.HOSPITAL;
          default:
            return ACTIVITY_LOCATION.OTHER;
        }
      } else {
        return ACTIVITY_LOCATION.OTHER;
      }
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET LOCATION:", err);
      return ACTIVITY_LOCATION.OTHER;
    }
  }

  /**
   * return activity category
   */
  public static getCategory(activity: Activity): ACTIVITY_CATEGORY {
    try {
      switch (activity.detail.category.coding[0].code) {
        case "diet":
          return ACTIVITY_CATEGORY.DIET;
        case "drug":
          return ACTIVITY_CATEGORY.DRUG;
        case "encounter":
          return ACTIVITY_CATEGORY.ENCOUNTER;
        case "observation":
          return ACTIVITY_CATEGORY.OBSERVATION;
        case "procedure":
          return ACTIVITY_CATEGORY.PROCEDURE;
        case "supply":
          return ACTIVITY_CATEGORY.SUPPLY;
        case "revalidation":
          return ACTIVITY_CATEGORY.REVALIDATION;
        default:
        case "other":
          return ACTIVITY_CATEGORY.OTHER;
      }
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "CANNOT GET CATEGORY:", err);
      return ACTIVITY_CATEGORY.OTHER;
    }
  }

  /**
   * Return snomed reference for this activity
   * @param careplan
   */
  public static getActivitySnomedRef(activity: Activity): string[] {
    const snomedRef: string[] = [];
    try {
      if (activity.reference) snomedRef.push(activity.reference.reference);
      if (activity.detail.productReference) snomedRef.push(activity.detail.productReference.reference);

      for (const coding of activity.detail.code.coding) {
        if (coding.system.includes("snomed") || coding.system.includes("comunicare.io")) snomedRef.push(coding.code);
      }
      // collect sub activities recomment
      if (activity.actionResulting && activity.actionResulting.length > 0) {
        for (const action of activity.actionResulting) {
          if (
            action.status === Careplan.ACTIVITY_STATUS_ACTIVE &&
            action.detail.target === ACTION_TARGET.RECOMMEND &&
            action.detail.reference.reference &&
            action.detail.reference.reference.length > 0
          ) {
            snomedRef.push(action.detail.reference.reference);
          }
        }
      }
      return snomedRef.filter(ArrayHelper.onlyNotFalsey).filter(ArrayHelper.onlyUnique);
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "getActivitySnomedRef failed", err);
      return snomedRef.filter(ArrayHelper.onlyNotFalsey).filter(ArrayHelper.onlyUnique);
    }
  }

  /**
   * Return snomed references for this careplan
   * @param careplan
   */
  public static getCareplanSnomedRef(careplan: ICareplan): string[] {
    const snomedRef: string[] = [];
    try {
      for (const coding of careplan.category[0].coding) {
        if (coding.system.includes("snomed") || coding.system.includes("comunicare.io")) snomedRef.push(coding.code);
      }
      return snomedRef.filter(ArrayHelper.onlyNotFalsey).filter(ArrayHelper.onlyUnique);
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "getCareplanSnomedRef failed", err);
      return snomedRef.filter(ArrayHelper.onlyNotFalsey).filter(ArrayHelper.onlyUnique);
    }
  }

  /**
   * Return snomed references for this careplan and its activities
   * @param careplan
   */
  public static getAllSnomedRef(careplan: ICareplan): string[] {
    let arrRef = new Array<string>();
    try {
      // get careplan reference
      const cpRef = FHIR_ActivityHelper.getCareplanSnomedRef(careplan);
      if (cpRef && cpRef.length > 0) arrRef = arrRef.concat(cpRef).filter(ArrayHelper.onlyUnique);
      // get activities reference
      for (const activity of careplan.activity) {
        const ref = FHIR_ActivityHelper.getActivitySnomedRef(activity);
        if (ref && ref.length > 0) arrRef = arrRef.concat(ref).filter(ArrayHelper.onlyUnique);
      }
      return arrRef;
    } catch (err) {
      FileLogger.error("FHIR_ActivityHelper", "getAllSnomedRef failed", err);
      return arrRef;
    }
  }
}
